import { ElementAttribute } from "../dom-tools/ElementAttribute";
import { ElementType } from "../dom-tools/ElementType";
import { Box2, Vector2 } from "three";
import { DOMNameSpaces } from "../dom-tools/DOMNameSpaces";

export class SVGEditor
{
  static readonly rect = new ElementType<SVGRectElement>( "rect", DOMNameSpaces.SVG );
  static readonly text = new ElementType<SVGTextElement>( "text", DOMNameSpaces.SVG );
  static readonly tspan = new ElementType<SVGTSpanElement>( "tspan", DOMNameSpaces.SVG );
  static readonly image = new ElementType<SVGImageElement>( "image", DOMNameSpaces.SVG );

  static readonly style = new ElementAttribute( "style", false, "" );
  static readonly transform = new ElementAttribute( "transform", false, "" );
  static readonly transformOrigin = new ElementAttribute( "transform-origin", false, "" );

  static readonly x = new ElementAttribute( "x", false, "" );
  static readonly y = new ElementAttribute( "y", false, "" );
  static readonly width = new ElementAttribute( "width", false, "" );
  static readonly height = new ElementAttribute( "height", false, "" );

  static readonly rx = new ElementAttribute( "rx", false, "" );
  static readonly ry = new ElementAttribute( "ry", false, "" );
  static readonly cx = new ElementAttribute( "cx", false, "" );
  static readonly cy = new ElementAttribute( "cy", false, "" );


  static readonly id = new ElementAttribute( "id", false, "" );
  static readonly class = new ElementAttribute( "class", false, "" );
  static readonly fontSize = new ElementAttribute( "font-size", false, "" );
  static readonly fontFamily = new ElementAttribute( "font-family", false, "" );
  static readonly fontWeight = new ElementAttribute( "font-weight", false, "" );
  static readonly fontStyle = new ElementAttribute( "font-style", false, "" ); 

  static readonly opacity = new ElementAttribute( "opacity", false );
  static readonly fill = new ElementAttribute( "fill", false );
  static readonly stroke = new ElementAttribute( "stroke", false );

  static readonly linearGradient = new ElementType( "linearGradient", DOMNameSpaces.SVG );
  static readonly radialGradient = new ElementType( "radialGradient", DOMNameSpaces.SVG );
  static readonly filter = new ElementType( "filter", DOMNameSpaces.SVG );
  static readonly clipPath = new ElementType( "clipPath", DOMNameSpaces.SVG );
  static readonly clipPathAttribute = new ElementAttribute( "clip-path", false, "" );

  static readonly viewBox = new ElementAttribute( "viewBox", false, "" );
  static readonly xlinkHref = new ElementAttribute( "xlink:href", false );
  
  static getViewBox( element:SVGElement )
  {
    let viewBoxValues = SVGEditor.viewBox.from( element ).split( " " );
    let min = new Vector2( parseFloat( viewBoxValues[ 0 ] ), parseFloat( viewBoxValues[ 1 ] ) );
    let max = new Vector2( parseFloat( viewBoxValues[ 2 ] ), parseFloat( viewBoxValues[ 3 ] ) );
    let box = new Box2( min, max );

    return box;
  }

  static setViewBox( element:SVGElement, box2:Box2 )
  {
    let minX = box2.min.x;
    let minY = box2.min.y;

    let maxX = box2.max.x;
    let maxY = box2.max.y;

    let viewBoxValues = `${minX} ${minY} ${maxX} ${maxY}`;
    SVGEditor.viewBox.to( element, viewBoxValues );
    SVGEditor.width.to( element, Math.round( maxX - minX ) + "" );
    SVGEditor.height.to( element, Math.round( maxY - minY ) + "" );

  }

  static translate( element:SVGElement, x:number, y:number )
  {
    SVGEditor.transform.to( element, `translate(${x},${y})` );
  }

  static setXY( element:SVGElement, xy:Vector2 )
  {
    SVGEditor.x.to( element, xy.x + "" );
    SVGEditor.y.to( element, xy.y + "" );
  }

  static getXY( element:SVGElement, output:Vector2)
  {    
    output.x = parseFloat( SVGEditor.x.from( element ) );
    output.y = parseFloat( SVGEditor.y.from( element ) );
  }

  static getSVGParent( element:Element )
  {
    let it = element;

    while ( it !== null )
    {

      if ( it.nodeName.toLowerCase() === "svg" )
      {
        return it;
      }

      it = it.parentElement;
    }

    return null;
  }

  static setText( textTarget:SVGElement, text:string )
  {
    let first = textTarget.querySelector( "tspan" );
    let nested = first.querySelector( "tspan" );
    
    let textNodeTarget = nested || first;

    textNodeTarget.innerHTML = text;
  }
  
  static getTranslateValue( transformValue:string ):Vector2
  {
    let regex = /translate\s*\(\s*(.+)\s*,\s*(.+)\s*\)/;
    let result = regex.exec( transformValue );

    if ( ! result || result.length !== 3 )
    {
      return new Vector2( 0, 0 );
    }

    return new Vector2( parseFloat( result[ 1 ] ) , parseFloat( result[ 2 ] ) );


  }

  static cloneDocument( svgDoc:Document )
  {
    return this.createDocumentfromString( new XMLSerializer().serializeToString( svgDoc ) );
  }

  static createDocumentfromString( svgString:string )
  {
    let parser = new DOMParser();
    let xmlDoc = parser.parseFromString( svgString, "text/xml");
    return xmlDoc;
  }


  private static _idCounter = 0;

  static readonly referenceTypes:ElementType<Element>[] = 
  [
    SVGEditor.clipPath,
    SVGEditor.radialGradient,
    SVGEditor.linearGradient,
    SVGEditor.filter
  ];

  static cloneSVGs( element:Element )
  {
    let svgs = ElementType.svg.queryAll( element );
    svgs.forEach( s => this.createUniqueIDs( s ) );
  }

  static createUniqueIDs( element:Element, prefix:string = null )
  {
    if ( ! prefix )
    {
      prefix = "id-" + SVGEditor._idCounter + "-";
      SVGEditor._idCounter ++; 
    }

    let ids = new Map<string,string>();

    this.referenceTypes.forEach(
      ( type )=>
      {
        let referenceElements = type.queryAll( element );

        for ( let i = 0; i < referenceElements.length; i++ )
        {
          let id = SVGEditor.id.from( referenceElements[ i ] )
          let newID = prefix + id;
          SVGEditor.id.to( referenceElements[ i ], newID )

          ids.set( id, newID );
        }
      }
    )
      
    

    let clipPathReplacements = SVGEditor.clipPathAttribute.queryAll( element );

    for ( let i = 0; i < clipPathReplacements.length; i++ )
    {
      let attributeValue = SVGEditor.clipPathAttribute.from( clipPathReplacements[ i ] )
      let replacedID = this._replaceLinkedID( attributeValue, prefix );
      SVGEditor.clipPathAttribute.to( clipPathReplacements[ i ], replacedID )
    }

    let styleReplacements = SVGEditor.style.queryAll( element );

    for ( let i = 0; i < styleReplacements.length; i++ )
    {
      this._replaceStyleValues( styleReplacements[ i ] as SVGElement, prefix );
    }

    let referencingElements = SVGEditor.xlinkHref.queryAll( element ); 
    
    referencingElements.forEach(
      re =>
      {
        let xlinkHrefValue = ElementAttribute.xlinkHref.from( re );

        if ( xlinkHrefValue === null )
        {
          xlinkHrefValue = re.getAttribute( "xlink:href" );
        }
        
        
        if ( xlinkHrefValue.startsWith( "data:") )
        {
          return;
        }


        if ( xlinkHrefValue === null )
        {
          return;
        }


        let id = xlinkHrefValue.replace( /^#/, "" );

        if ( id.length > 500 )
        {
          return;
        }
        
        if ( ids.has( id ) )
        {
          
          ElementAttribute.xlinkHref.to( re, "#" + ids.get( id ) );
          return;
        }

      }
    );


  }

  static _replaceStyleValues( element:SVGElement, prefix:string )
  {
    let style = element.style;
    style.fill   = SVGEditor._replaceLinkedID( style.fill, prefix );
    style.stroke = SVGEditor._replaceLinkedID( style.stroke, prefix );
    style.filter = SVGEditor._replaceLinkedID( style.filter, prefix );
    
    SVGEditor.style.to( element, SVGEditor.style.from( element ) );
  }

  static _replaceLinkedID( value:string, prefix:string )
  {
    let urlRegex = /url\((.+)\)/;

    var result = urlRegex.exec( value );

    if ( ! result )
    {
      return value;
    }

    var id = result[ 1 ];

    id = id.replace( /(^("|')#)|(^#)/,"");
    id = id.replace( /("|')$/,"");

    var newID = `url(#${ prefix + id })`
    
    return newID;    
  } 

  static createDocument( width:number, height:number )
  {
    let svgDocumentString = 
    `<svg
      width="${width}"
      height="${height}"
      viewBox="0 0 ${width} ${height}"
      version="1.1"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:svg="http://www.w3.org/2000/svg"
     >
      <defs></defs>
      <g></g>
     </svg>
    `;

    return this.createDocumentfromString( svgDocumentString );
  }
}